import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, X, Power } from "lucide-react";

const empty = {
  code: "",
  kind: "percent",
  value: "10",
  min_subtotal: "0",
  active: true,
  expires_at: "",
};

const AdminDiscounts = () => {
  const [discounts, setDiscounts] = useState([]);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/discounts").then((r) => setDiscounts(r.data || []));
  useEffect(() => {
    load();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        code: editor.code.trim().toUpperCase(),
        kind: editor.kind,
        value: Number(editor.value),
        min_subtotal: Number(editor.min_subtotal || 0),
        active: !!editor.active,
        expires_at: editor.expires_at ? new Date(editor.expires_at).toISOString() : null,
      };
      await api.post("/admin/discounts", payload);
      toast.success(`Code ${payload.code} created`);
      setEditor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.patch(`/admin/discounts/${d.discount_id}`, { active: !d.active });
      load();
    } catch {
      toast.error("Update failed");
    }
  };
  const onDelete = async (d) => {
    if (!confirm(`Delete code ${d.code}?`)) return;
    try {
      await api.delete(`/admin/discounts/${d.discount_id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-discounts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-12">
        <div>
          <div className="overline mb-3">Quiet Generosity</div>
          <h1 className="font-serif-display text-5xl text-bone">Discount codes</h1>
        </div>
        <button
          data-testid="admin-discount-new-btn"
          onClick={() => setEditor({ ...empty })}
          className="px-8 py-3 uppercase text-xs tracking-[0.22em] text-bone flex items-center gap-2"
          style={{ background: "var(--rs-oxblood)" }}
        >
          <Plus strokeWidth={1.5} size={14} /> New code
        </button>
      </div>

      <div className="border" style={{ borderColor: "var(--rs-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-[0.22em] text-muted-bone">
              <th className="text-left px-6 py-4">Code</th>
              <th className="text-left px-6 py-4">Type</th>
              <th className="text-left px-6 py-4">Value</th>
              <th className="text-left px-6 py-4">Min subtotal</th>
              <th className="text-left px-6 py-4">Status</th>
              <th className="text-right px-6 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {discounts.map((d) => (
              <tr
                key={d.discount_id}
                data-testid={`admin-discount-row-${d.code}`}
                className="border-t"
                style={{ borderColor: "var(--rs-border)" }}
              >
                <td className="px-6 py-4 text-bone font-mono">{d.code}</td>
                <td className="px-6 py-4 text-muted-bone uppercase tracking-widest text-xs">{d.kind}</td>
                <td className="px-6 py-4 text-bone">{d.kind === "percent" ? `${d.value}%` : `$${d.value.toFixed(2)}`}</td>
                <td className="px-6 py-4 text-muted-bone">${(d.min_subtotal || 0).toFixed(2)}</td>
                <td className="px-6 py-4">
                  <span
                    className="px-2 py-1 text-[10px] uppercase tracking-widest"
                    style={{ background: d.active ? "var(--rs-oxblood)" : "transparent", color: d.active ? "var(--rs-bone)" : "var(--rs-muted)", border: d.active ? "none" : "1px solid var(--rs-border)" }}
                  >
                    {d.active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-3">
                    <button
                      data-testid={`admin-discount-toggle-${d.code}`}
                      onClick={() => toggle(d)}
                      className="text-muted-bone hover:text-bone"
                      aria-label="toggle"
                    >
                      <Power strokeWidth={1.5} size={16} />
                    </button>
                    <button
                      data-testid={`admin-discount-delete-${d.code}`}
                      onClick={() => onDelete(d)}
                      className="text-muted-bone hover:text-bone"
                      aria-label="delete"
                    >
                      <Trash2 strokeWidth={1.5} size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {discounts.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-10 text-center text-muted-bone">
                  No codes yet. Create one to invite favoured guests.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editor && (
        <div
          data-testid="admin-discount-editor"
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,10,0.92)" }}
        >
          <div
            className="w-full max-w-lg border"
            style={{ background: "var(--rs-surface)", borderColor: "var(--rs-border)" }}
          >
            <div
              className="flex items-center justify-between px-6 py-5 border-b"
              style={{ borderColor: "var(--rs-border)" }}
            >
              <div className="font-serif-display text-2xl text-bone">New discount code</div>
              <button onClick={() => setEditor(null)} className="text-muted-bone hover:text-bone">
                <X strokeWidth={1.5} size={18} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-4">
              <label className="block">
                <span className="overline block mb-2">Code</span>
                <input
                  data-testid="admin-discount-code-input"
                  required
                  value={editor.code}
                  onChange={(e) => setEditor({ ...editor, code: e.target.value.toUpperCase() })}
                  placeholder="WELCOME10"
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none font-mono"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline block mb-2">Type</span>
                  <select
                    data-testid="admin-discount-kind-select"
                    value={editor.kind}
                    onChange={(e) => setEditor({ ...editor, kind: e.target.value })}
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  >
                    <option value="percent">Percent off</option>
                    <option value="fixed">Fixed amount off</option>
                  </select>
                </label>
                <label className="block">
                  <span className="overline block mb-2">{editor.kind === "percent" ? "Percent" : "Amount ($)"}</span>
                  <input
                    data-testid="admin-discount-value-input"
                    required
                    type="number"
                    step="0.01"
                    min="0"
                    value={editor.value}
                    onChange={(e) => setEditor({ ...editor, value: e.target.value })}
                    className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                    style={{ borderColor: "var(--rs-border)" }}
                  />
                </label>
              </div>
              <label className="block">
                <span className="overline block mb-2">Minimum subtotal ($)</span>
                <input
                  data-testid="admin-discount-min-input"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editor.min_subtotal}
                  onChange={(e) => setEditor({ ...editor, min_subtotal: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <label className="block">
                <span className="overline block mb-2">Expires at (optional)</span>
                <input
                  data-testid="admin-discount-expires-input"
                  type="datetime-local"
                  value={editor.expires_at}
                  onChange={(e) => setEditor({ ...editor, expires_at: e.target.value })}
                  className="w-full px-3 py-3 bg-transparent border text-bone outline-none"
                  style={{ borderColor: "var(--rs-border)" }}
                />
              </label>
              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={!!editor.active}
                  onChange={(e) => setEditor({ ...editor, active: e.target.checked })}
                />
                <span className="overline">Active</span>
              </label>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setEditor(null)}
                  className="px-6 py-3 text-xs uppercase tracking-[0.22em] text-muted-bone border"
                  style={{ borderColor: "var(--rs-border)" }}
                >
                  Cancel
                </button>
                <button
                  data-testid="admin-discount-save-btn"
                  type="submit"
                  disabled={saving}
                  className="px-8 py-3 text-xs uppercase tracking-[0.22em] text-bone disabled:opacity-50"
                  style={{ background: "var(--rs-oxblood)" }}
                >
                  {saving ? "Creating…" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDiscounts;
